import {LightTooltip} from '@ui/LightTooltip/LightTooltip';
import {ColumnData} from './types';
import {useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {selectDictionaries} from 'slices';

type TableCellTooltipProps = {
    value: string | number | null;
    children?: React.ReactElement;
    column: ColumnData;
    style?: React.CSSProperties;
};

const TableCellTooltip = ({value, column, children, style}: TableCellTooltipProps) => {
    const [formattedValue, setFormattedValue] = useState(value);

    const dictionaries = useSelector(selectDictionaries);
    const cellDictionary = useMemo(
        () => (column?.dictName ? dictionaries[column?.dictName] : []),
        [column?.dictName, dictionaries],
    );

    useEffect(() => {
        if (!!cellDictionary?.length) {
            const newValue = cellDictionary.find((item) => item.code === value)?.displayName;
            if (newValue) {
                setFormattedValue(newValue);
                return;
            }
        }
        setFormattedValue(value);
    }, [cellDictionary, value]);

    if (formattedValue === undefined || value === null) {
        return null;
    }

    if (!column.width) {
        return children || formattedValue;
    }

    if (String(formattedValue).length < column.width / 10) {
        return children || formattedValue;
    }

    return (
        <LightTooltip
            title={formattedValue}
            followCursor={false}
            offset={[-3, -25]}
            slotProps={{
                tooltip: {
                    sx: {
                        fontSize: ' 0.875rem',
                        fontWeight: 400,
                        width: column.width - 10,
                        whiteSpace: 'normal',
                    },
                },
            }}
            sx={{...style}}
        >
            {children || <span>{formattedValue}</span>}
        </LightTooltip>
    );
};

export default TableCellTooltip;
